/**
 * A touch long press as a `contextmenu` event.
 *
 * Android fires `contextmenu` for a long press, iOS never does - and every
 * right-click menu in the app listens for that event: the Radix context menus,
 * the timeline's selection menu, the picture's object menu. So one is dispatched
 * at the press point wherever the platform does not, and whichever arrives first
 * is the only one any handler hears.
 *
 * A press that opened a menu takes the rest of itself with it: the text
 * selection the platform starts on the same hold, and the click its release
 * makes on the row under the finger.
 */

// Short of the hold at which iOS starts selecting text, so the menu is first.
const LONG_PRESS_MS = 450;
// How far a finger may drift while held and still be pressing, not dragging.
const DRAG_THRESHOLD_PX = 10;
// The click a release makes lands within a frame or two of it.
const RELEASE_CLICK_MS = 400;
const CONTEXT_MENU_BUTTON = 2;

// A long press there belongs to the field: it places the caret and pastes.
const EDITABLE_SELECTOR =
    'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

/** The finger being held. */
interface Press {
    pointerId: number;
    target: Element;
    x: number;
    y: number;
    timer: ReturnType<typeof setTimeout>;
    /** A `contextmenu` went out for it, ours or the platform's. */
    hasFired: boolean;
    /** A handler took it, so the hold opened something. */
    isHandled: boolean;
}

/** Starts listening on `win`; the returned function stops. */
export function installLongPressContextMenu(win: Window = window): () => void {
    const touches = new Set<number>();
    let press: Press | null = null;
    let suppressClicksUntilMs = 0;
    // Set while our own `contextmenu` is out, so the listener below can tell it
    // from the platform's.
    let isDispatching = false;

    const drop = (): void => {
        if (press !== null) {
            clearTimeout(press.timer);
            press = null;
        }
    };

    const fire = (): void => {
        const currentPress = press;
        if (currentPress === null || currentPress.hasFired) {
            return;
        }
        currentPress.hasFired = true;
        const event = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            composed: true,
            clientX: currentPress.x,
            clientY: currentPress.y,
            button: CONTEXT_MENU_BUTTON,
        });
        isDispatching = true;
        try {
            currentPress.isHandled = !currentPress.target.dispatchEvent(event);
        } finally {
            isDispatching = false;
        }
        if (currentPress.isHandled) {
            win.getSelection()?.removeAllRanges();
        }
    };

    const onPointerDown = (event: PointerEvent): void => {
        if (event.pointerType !== 'touch') {
            return;
        }
        touches.add(event.pointerId);
        // A second finger is a pinch, not a hold.
        drop();
        const { target } = event;
        if (touches.size > 1 || !(target instanceof Element) ||
            target.closest(EDITABLE_SELECTOR)) {
            return;
        }
        press = {
            pointerId: event.pointerId,
            target,
            x: event.clientX,
            y: event.clientY,
            timer: setTimeout(fire, LONG_PRESS_MS),
            hasFired: false,
            isHandled: false,
        };
    };

    const onPointerMove = (event: PointerEvent): void => {
        if (press === null || event.pointerId !== press.pointerId || press.hasFired) {
            return;
        }
        if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > DRAG_THRESHOLD_PX) {
            drop();
        }
    };

    const onPointerEnd = (event: PointerEvent): void => {
        touches.delete(event.pointerId);
        if (press === null || event.pointerId !== press.pointerId) {
            return;
        }
        if (press.isHandled && event.type === 'pointerup') {
            suppressClicksUntilMs = performance.now() + RELEASE_CLICK_MS;
        }
        drop();
    };

    // The platform's own event, for a hold it recognised first: ours stands down.
    // One arriving after ours is the same hold heard twice.
    const onContextMenu = (event: MouseEvent): void => {
        if (isDispatching || press === null) {
            return;
        }
        if (press.hasFired) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }
        clearTimeout(press.timer);
        press.hasFired = true;
    };

    const onSelectStart = (event: Event): void => {
        if (press?.isHandled) {
            event.preventDefault();
        }
    };

    const onClick = (event: MouseEvent): void => {
        if (performance.now() < suppressClicksUntilMs) {
            suppressClicksUntilMs = 0;
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const listeningAbortController = new AbortController();
    const options = { capture: true, signal: listeningAbortController.signal };
    win.addEventListener('pointerdown', onPointerDown, options);
    win.addEventListener('pointermove', onPointerMove, options);
    win.addEventListener('pointerup', onPointerEnd, options);
    win.addEventListener('pointercancel', onPointerEnd, options);
    win.addEventListener('contextmenu', onContextMenu, options);
    win.addEventListener('selectstart', onSelectStart, options);
    win.addEventListener('click', onClick, options);
    return (): void => {
        drop();
        listeningAbortController.abort();
    };
}
